import React, { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AuthCard } from "../../components/auth/AuthCard";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { useLearner } from "../../contexts/LearnerContext";
import { shakeKeyframes, shakeTransition } from "../../utils/motion";
export function VerifyEmail() {
  const navigate = useNavigate();
  const {
    user
  } = useLearner();
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(0);
  const [cooldown, setCooldown] = useState(20);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [cooldown]);
  const verify = (code: string) => {
    setLoading(true);
    setError("");
    window.setTimeout(() => {
      setLoading(false);
      if (code === "000000") {
        setError("That code isn't right. Check the latest email and try again.");
        setShake(s => s + 1);
        setDigits(Array(6).fill(""));
        refs.current[0]?.focus();
        return;
      }
      navigate("/app");
    }, 800);
  };
  const setAt = (i: number, v: string) => {
    const d = v.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = d;
    setDigits(next);
    if (d && i < 5) refs.current[i + 1]?.focus();
    if (next.every(Boolean)) verify(next.join(""));
  };
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = Array.from({
      length: 6
    }, (_, i) => text[i] ?? "");
    setDigits(next);
    refs.current[Math.min(text.length, 5)]?.focus();
    if (text.length === 6) verify(text);
  };
  return <AuthCard title="Verify your email" description={<>
          Enter the 6-digit code we sent to <strong className="text-ink" data-id="element-1729" data-mp="fza5wutzohs3">{user.email}</strong>. (Demo: any code works, 000000 fails.)
        </>} data-id="element-1728" data-mp="kxbqxlaz6r67">
      <motion.div key={shake} animate={shake ? shakeKeyframes : undefined} transition={shakeTransition} className="flex justify-between gap-2" data-id="element-1730" data-mp="1cpj5mg6x7thy">
        {digits.map((d, i) => <input key={i} ref={el => refs.current[i] = el} value={d} inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} aria-label={`Digit ${i + 1}`} maxLength={1} onChange={e => setAt(i, e.target.value)} onKeyDown={e => onKey(i, e)} onPaste={onPaste} disabled={loading} className={`h-14 w-full min-w-0 rounded-xl border-3 text-center text-2xl font-bold text-ink outline-none transition-[transform,box-shadow] duration-150 ease-out focus:-translate-y-0.5 focus:shadow-brutal-sm ${error ? "border-bad bg-bad-tint" : d ? "border-line bg-brand-tint" : "border-line bg-surface"}`} data-id="element-1731" data-mp="13tk0v2exnq7u" data-mpi={i} />)}
      </motion.div>
      {error && <p role="alert" className="mt-3 text-sm font-medium text-bad" data-id="element-1732" data-mp="2mtszfiehpnmp">
          {error}
        </p>}
      <div className="mt-6" data-id="element-1733" data-mp="2rk9c30ahl580">
        <BrutalButton full size="lg" loading={loading} disabled={!digits.every(Boolean)} onClick={() => verify(digits.join(""))} data-id="element-1734" data-mp="2rm3y6hxhv3do">
          {loading ? "Checking…" : "Verify"}
        </BrutalButton>
      </div>
      <button type="button" disabled={cooldown > 0} onClick={() => setCooldown(20)} className="focus-ring mt-4 w-full rounded-lg py-2 text-sm font-semibold text-ink-2 underline underline-offset-4 disabled:no-underline disabled:opacity-60" data-id="element-1735" data-mp="30n25b938br0h">
        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
      </button>
    </AuthCard>;
}